import { standardiseVehicleId } from './vehicleIdStandardise';
import type { TruthUtilisationVehicleDayRow } from '../types/utilisationTruth';

export type PeriodType = 'day' | 'week' | 'month';

export interface WeekPeriod {
  startDate: string;
  endDate: string;
  label?: string;
}

export function buildUtilisationLookup(
  records: TruthUtilisationVehicleDayRow[],
): Map<string, TruthUtilisationVehicleDayRow> {
  const map = new Map<string, TruthUtilisationVehicleDayRow>();
  for (const r of records) {
    const date = r.date.slice(0, 10);
    const vrn = standardiseVehicleId(r.vehicle) || (r.vehicle || '').trim();
    if (vrn) map.set(`${date}|${vrn}`, r);
    if (r.vehicle_entity_id) map.set(`${date}|${r.vehicle_entity_id}`, r);
  }
  return map;
}

export function formatUtilisationTooltip(row: TruthUtilisationVehicleDayRow): string {
  const parts = [
    `Utilisation score: ${row.utilisation_score?.toFixed(1) ?? '—'}%`,
    `Mileage: ${row.mileage_efficiency_pct?.toFixed(1) ?? '—'}%`,
    `Time: ${row.time_efficiency_pct?.toFixed(1) ?? '—'}%`,
  ];
  if (row.revenue_efficiency_pct != null) {
    parts.push(`Revenue: ${row.revenue_efficiency_pct.toFixed(1)}%`);
  }
  if (row.load_efficiency_pct != null) {
    parts.push(`Load: ${row.load_efficiency_pct.toFixed(1)}%`);
  }
  return parts.join(' · ');
}

function periodBoundsForDate(
  dateOnly: string,
  periodType: PeriodType,
  availableWeeks: WeekPeriod[],
): { start: string; end: string } | null {
  if (!dateOnly) return null;
  if (periodType === 'day') return { start: dateOnly, end: dateOnly };
  if (periodType === 'week') {
    const w = availableWeeks.find((wk) => dateOnly >= wk.startDate && dateOnly <= wk.endDate);
    return w ? { start: w.startDate, end: w.endDate } : null;
  }
  if (periodType === 'month') {
    const ym = dateOnly.length >= 7 ? dateOnly.slice(0, 7) : dateOnly;
    const [y, m] = ym.split('-').map((x) => parseInt(x, 10));
    if (!y || !m) return null;
    const lastDay = new Date(y, m, 0).getDate();
    return {
      start: `${ym}-01`,
      end: `${ym}-${String(lastDay).padStart(2, '0')}`,
    };
  }
  return { start: dateOnly, end: dateOnly };
}

/**
 * Resolve utilisation_score for a Daily Vehicle Summary row.
 * Day view: exact vehicle-day. Week/month: simple mean over days in the bucket.
 */
export function resolveUtilisationForSummaryRow(params: {
  date: string;
  vehicleId: string;
  periodType: PeriodType;
  availableWeeks: WeekPeriod[];
  lookup: Map<string, TruthUtilisationVehicleDayRow>;
  allRecords: TruthUtilisationVehicleDayRow[];
}): { score: number | null; tooltip: string | null; sampleRow: TruthUtilisationVehicleDayRow | null } {
  const { date, vehicleId, periodType, availableWeeks, lookup, allRecords } = params;
  const dateOnly = date.slice(0, 10);
  const stdVid = standardiseVehicleId(vehicleId) || vehicleId.trim();
  if (!stdVid || stdVid === '—') {
    return { score: null, tooltip: null, sampleRow: null };
  }

  if (periodType === 'day') {
    const row = lookup.get(`${dateOnly}|${stdVid}`) ?? null;
    if (!row) return { score: null, tooltip: null, sampleRow: null };
    return {
      score: row.utilisation_score,
      tooltip: formatUtilisationTooltip(row),
      sampleRow: row,
    };
  }

  const bounds = periodBoundsForDate(dateOnly, periodType, availableWeeks);
  if (!bounds) return { score: null, tooltip: null, sampleRow: null };

  const matches = allRecords.filter((r) => {
    const d = r.date.slice(0, 10);
    if (d < bounds.start || d > bounds.end) return false;
    const vrn = standardiseVehicleId(r.vehicle) || (r.vehicle || '').trim();
    return vrn === stdVid;
  });

  if (matches.length === 0) {
    return { score: null, tooltip: null, sampleRow: null };
  }

  const score =
    Math.round((matches.reduce((s, r) => s + (r.utilisation_score ?? 0), 0) / matches.length) * 100) /
    100;

  return {
    score,
    tooltip: `Average daily utilisation score across ${matches.length} day(s) in this period.`,
    sampleRow: matches[0],
  };
}

/** Per-vehicle rollup for Monday Brief (mean score + totals over vehicle-days in range). */
export interface MondayBriefVehicleUtilisation {
  utilisation_score: number;
  mileage_efficiency_pct?: number | null;
  time_efficiency_pct?: number | null;
  revenue_efficiency_pct?: number | null;
  active_hours: number;
  downtime_hours: number;
  num_trips: number;
  vehicleDays: number;
}

function inDateRange(date: string, start: string, end: string): boolean {
  const d = date.slice(0, 10);
  return d >= start && d <= end;
}

function mean(nums: number[]): number | null {
  if (nums.length === 0) return null;
  return nums.reduce((s, n) => s + n, 0) / nums.length;
}

export function buildMondayBriefUtilisationByVehicle(
  records: TruthUtilisationVehicleDayRow[],
  startDate: string,
  endDate: string,
): Map<string, MondayBriefVehicleUtilisation> {
  const byVehicle = new Map<
    string,
    {
      scores: number[];
      mileage: number[];
      time: number[];
      revenue: number[];
      active: number;
      downtime: number;
      trips: number;
    }
  >();

  for (const r of records) {
    if (!inDateRange(r.date, startDate, endDate)) continue;
    const vrn = standardiseVehicleId(r.vehicle) || (r.vehicle || '').trim();
    if (!vrn) continue;

    const bucket = byVehicle.get(vrn) ?? {
      scores: [],
      mileage: [],
      time: [],
      revenue: [],
      active: 0,
      downtime: 0,
      trips: 0,
    };
    if (r.utilisation_score != null && Number.isFinite(r.utilisation_score)) {
      bucket.scores.push(r.utilisation_score);
    }
    if (r.mileage_efficiency_pct != null && Number.isFinite(r.mileage_efficiency_pct)) {
      bucket.mileage.push(r.mileage_efficiency_pct);
    }
    if (r.time_efficiency_pct != null && Number.isFinite(r.time_efficiency_pct)) {
      bucket.time.push(r.time_efficiency_pct);
    }
    if (r.revenue_efficiency_pct != null && Number.isFinite(r.revenue_efficiency_pct)) {
      bucket.revenue.push(r.revenue_efficiency_pct);
    }
    bucket.active += r.active_hours ?? 0;
    bucket.downtime += r.downtime_hours ?? 0;
    bucket.trips += r.num_trips ?? 0;
    byVehicle.set(vrn, bucket);
  }

  const out = new Map<string, MondayBriefVehicleUtilisation>();
  for (const [vrn, b] of byVehicle.entries()) {
    const scoreMean = mean(b.scores);
    if (scoreMean == null) continue;
    out.set(vrn, {
      utilisation_score: Math.round(scoreMean * 100) / 100,
      mileage_efficiency_pct: mean(b.mileage),
      time_efficiency_pct: mean(b.time),
      revenue_efficiency_pct: mean(b.revenue),
      active_hours: b.active,
      downtime_hours: b.downtime,
      num_trips: b.trips,
      vehicleDays: b.scores.length,
    });
  }
  return out;
}

/** Fleet-wide mean utilisation_score across vehicle-days in range (simple mean for v1). */
export function computeFleetUtilisationScoreMean(
  records: TruthUtilisationVehicleDayRow[],
  startDate: string,
  endDate: string,
): number | null {
  const scores: number[] = [];
  for (const r of records) {
    if (!inDateRange(r.date, startDate, endDate)) continue;
    if (r.utilisation_score != null && Number.isFinite(r.utilisation_score)) {
      scores.push(r.utilisation_score);
    }
  }
  const m = mean(scores);
  return m == null ? null : Math.round(m * 100) / 100;
}

/** Mean utilisation_score grouped by vehicle type + date (fleet type rollup). */
export function rollupUtilisationScoreByTypeAndDate(
  rows: Array<{ date: string; vehicleType: string; utilisationScore: number }>,
): Map<string, number> {
  const buckets = new Map<string, number[]>();
  for (const row of rows) {
    const date = row.date.slice(0, 10);
    const type = row.vehicleType.trim();
    if (!date || !type) continue;
    const key = `${date}|${type}`;
    const list = buckets.get(key) ?? [];
    list.push(row.utilisationScore);
    buckets.set(key, list);
  }
  const out = new Map<string, number>();
  for (const [key, scores] of buckets.entries()) {
    const m = mean(scores);
    if (m != null) out.set(key, Math.round(m * 100) / 100);
  }
  return out;
}
